import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Productos from './pages/Productos';
import Categorias from './pages/Categorias';
import Marcas from './pages/Marcas';
import Proveedores from './pages/Proveedores';
import Compras from './pages/Compras';
import Inventario from './pages/Inventario';
import Kardex from './pages/Kardex';
import Configuracion from './pages/Configuracion';
import Clientes from './pages/Clientes';
import HistoriaClinica from './pages/HistoriaClinica';
import HistoriasClinicasList from './pages/HistoriasClinicasList';
import Ventas from './pages/Ventas';
import OrdenTrabajo from './pages/OrdenTrabajo';
import Caja from './pages/Caja';
import Sucursales from './pages/Sucursales';
import Usuarios from './pages/Usuarios';
import ApiDocs from './pages/ApiDocs';
import FacturacionElectronica from './pages/FacturacionElectronica';
import ServiciosCatalogo from './pages/ServiciosCatalogo';
import PuntoVentaServicios from './pages/PuntoVentaServicios';
import AtencionesList from './pages/AtencionesList';
import Certificados from './pages/Certificados';
import Transferencias from './pages/Transferencias';
import PayphoneCallback from './pages/PayphoneCallback';
import CuentasPorCobrar from './pages/CuentasPorCobrar';
import Gastos from './pages/Gastos';
import GastosDiarios from './pages/GastosDiarios';
import Reportes from './pages/Reportes';
import ActividadEquipo from './pages/ActividadEquipo';
import ConciliacionBancaria from './pages/ConciliacionBancaria';
import ManualUsuario from './pages/ManualUsuario';
import AnalisisIA from './pages/AnalisisIA';

// FIX: App component defines all application routes.
// All routes except /login are protected via ProtectedRoute wrapper on the Layout.
// Routes requiring specific permissions use nested ProtectedRoute with permiso prop.
function App() {
  const { loading, isAuthenticated } = useAuth();

  // FIX: Show loading spinner while auth state is being determined (token validation)
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="text-muted">Cargando sistema...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Routes>
        {/* FIX: Login is a public route; authenticated users are redirected to Dashboard */}
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to="/" replace /> : <Login />
          }
        />

        {/* FIX: All protected routes are nested under Layout with ProtectedRoute guard.
            This ensures authentication is checked once at the Layout level.
            Individual routes with permission requirements use nested ProtectedRoute. */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          {/* Dashboard */}
          <Route index element={<Dashboard />} />

          {/* Productos y Catálogos */}
          <Route path="productos" element={<Productos />} />
          <Route path="categorias" element={<Categorias />} />
          <Route path="marcas" element={<Marcas />} />
          <Route path="proveedores" element={<Proveedores />} />

          {/* Inventario y Compras */}
          <Route path="compras" element={<Compras />} />
          <Route path="inventario" element={<Inventario />} />
          <Route path="transferencias" element={<Transferencias />} />
          <Route path="kardex/:productoId" element={<Kardex />} />

          {/* Pacientes y Clínica */}
          <Route path="clientes" element={<Clientes />} />
          <Route path="historias-clinicas" element={<HistoriasClinicasList />} />
          <Route path="historia-clinica/nueva" element={<HistoriaClinica />} />
          <Route path="historia-clinica/:id" element={<HistoriaClinica />} />
          <Route path="ordenes-trabajo" element={<OrdenTrabajo />} />
          <Route path="certificados" element={
            <ProtectedRoute permiso="certificados">
              <Certificados />
            </ProtectedRoute>
          } />

          {/* Punto de Venta y Caja */}
          <Route path="ventas" element={<Ventas />} />
          <Route path="caja" element={<Caja />} />
          <Route path="cuentas-por-cobrar" element={<CuentasPorCobrar />} />
          <Route path="gastos" element={<Gastos />} />
          <Route path="gastos-diarios" element={<GastosDiarios />} />
          <Route path="facturacion" element={<FacturacionElectronica />} />

          {/* Facturación de Servicios (comprobantes internos NO-SRI) */}
          <Route path="servicios-catalogo" element={
            <ProtectedRoute permiso="servicios">
              <ServiciosCatalogo />
            </ProtectedRoute>
          } />
          <Route path="pos-servicios" element={
            <ProtectedRoute permiso="atenciones">
              <PuntoVentaServicios />
            </ProtectedRoute>
          } />
          <Route path="atenciones" element={
            <ProtectedRoute permiso="atenciones">
              <AtencionesList />
            </ProtectedRoute>
          } />

          {/* Reportes */}
          <Route path="reportes" element={<Reportes />} />
          <Route path="actividad-equipo" element={<ActividadEquipo />} />
          <Route path="conciliacion-bancaria" element={<ConciliacionBancaria />} />

          {/* FIX: Análisis IA requires admin_only permission via nested ProtectedRoute */}
          <Route path="analisis-ia" element={
            <ProtectedRoute permiso="admin_only">
              <AnalisisIA />
            </ProtectedRoute>
          } />

          {/* Ayuda */}
          <Route path="manual" element={<ManualUsuario />} />

          {/* Payphone payment callback */}
          <Route path="payphone-callback" element={<PayphoneCallback />} />

          {/* FIX: Admin routes — each requires specific permission */}
          <Route path="configuracion" element={
            <ProtectedRoute permiso="configuracion">
              <Configuracion />
            </ProtectedRoute>
          } />
          <Route path="sucursales" element={
            <ProtectedRoute permiso="sucursales">
              <Sucursales />
            </ProtectedRoute>
          } />
          <Route path="usuarios" element={
            <ProtectedRoute permiso="usuarios">
              <Usuarios />
            </ProtectedRoute>
          } />
          <Route path="api-docs" element={
            <ProtectedRoute permiso="api_docs">
              <ApiDocs />
            </ProtectedRoute>
          } />
        </Route>

        {/* FIX: Catch-all 404 route — redirects unknown paths to Dashboard.
            This handles typos, bookmarks to removed pages, and direct URL entry. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
}

export default App;
